import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { FileText, Upload, Trash2, Loader2, BookOpen, ChevronDown, ChevronRight, Image as ImageIcon } from 'lucide-react';

/**
 * Per-brand Reference Library.
 * Upload PDFs / images / text that the AI reads as authoritative brand source
 * material when generating hooks, posts, scripts, carousels and threads.
 * PDFs are parsed to text on the backend; that text is injected into every
 * generation prompt for this brand (company/carrier names are never output).
 */
export function BrandReferenceLibrary({ api, profileId, profileName }) {
  const [open, setOpen] = useState(false);
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/admin/social-studio/brand-profiles/${profileId}/references`);
      setRefs(r.data.references || []);
    } catch (e) {
      toast.error('Reference load failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [api, profileId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (note.trim()) fd.append('note', note.trim());
      await api.post(`/api/admin/social-studio/brand-profiles/${profileId}/references`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success('Reference added — the AI will now use it for this brand');
      setNote('');
      await load();
    } catch (e) {
      toast.error('Upload failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/api/admin/social-studio/references/${id}`);
      setRefs((cur) => cur.filter((r) => r.reference_id !== id));
      toast.success('Removed');
    } catch (e) {
      toast.error('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const onToggle = async (r) => {
    try {
      await api.patch(`/api/admin/social-studio/references/${r.reference_id}`, { enabled: !r.enabled });
      setRefs((cur) => cur.map((x) => (x.reference_id === r.reference_id ? { ...x, enabled: !x.enabled } : x)));
    } catch (e) {
      toast.error('Update failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const enabledCount = refs.filter((r) => r.enabled !== false).length;

  return (
    <div className="mt-3 border-t border-[#F1F5F9] pt-3" data-testid={`reference-library-${profileId}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-xs font-semibold text-[#0A0F1C] hover:text-[#1D4ED8] transition-colors"
        data-testid={`reference-toggle-${profileId}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <BookOpen size={13} className="text-[#1D4ED8]" aria-hidden="true" />
          Reference Library
          {refs.length > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]">{refs.length}</span>
          )}
        </span>
        {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`reference-panel-${profileId}`}>
          <p className="text-[11px] text-[#64748B] leading-snug">
            Upload brochures, fact sheets or talking points (PDF, image or .txt). The AI reads the text and
            speaks your industry's language when it writes — and never names a specific company.
          </p>

          {/* Upload */}
          <div className="bg-[#F8FAFC] border border-dashed border-[#CBD5E1] rounded-xl p-3 space-y-2">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note (e.g. 'Funeral cost inflation stats')"
              className="w-full px-2.5 py-1.5 border border-[#E2E8F0] rounded-lg text-xs focus:outline-none focus:border-[#1D4ED8]"
              aria-label="Reference note"
              data-testid={`reference-note-${profileId}`}
            />
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.txt,.md,image/*"
                onChange={(e) => onUpload(e.target.files?.[0])}
                disabled={uploading}
                className="hidden"
                id={`ref-file-${profileId}`}
                data-testid={`reference-file-input-${profileId}`}
              />
              <label
                htmlFor={`ref-file-${profileId}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
                  uploading ? 'bg-[#E2E8F0] text-[#94A3B8] cursor-wait' : 'bg-[#1D4ED8] text-white hover:bg-[#1e40af]'
                }`}
                data-testid={`reference-upload-btn-${profileId}`}
              >
                {uploading ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Upload size={13} aria-hidden="true" />}
                {uploading ? 'Uploading…' : 'Upload file'}
              </label>
              <span className="text-[10px] text-[#94A3B8]">PDF · image · txt · max 15MB</span>
            </div>
          </div>

          {/* List */}
          {loading ? (
            <div className="text-xs text-[#94A3B8] py-2 inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" aria-hidden="true" /> Loading…
            </div>
          ) : refs.length === 0 ? (
            <p className="text-xs text-[#94A3B8] py-1">No reference material yet.</p>
          ) : (
            <ul className="space-y-1.5" data-testid={`reference-list-${profileId}`}>
              {refs.map((r) => (
                <li
                  key={r.reference_id}
                  className="flex items-center justify-between gap-2 bg-white border border-[#E2E8F0] rounded-lg px-2.5 py-1.5"
                  data-testid={`reference-item-${r.reference_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {r.kind === 'image' ? (
                      <ImageIcon size={14} className="text-[#7C3AED] shrink-0" aria-hidden="true" />
                    ) : (
                      <FileText size={14} className="text-[#1D4ED8] shrink-0" aria-hidden="true" />
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-[#0A0F1C] truncate" title={r.original_filename}>{r.original_filename}</p>
                      <p className="text-[10px] text-[#94A3B8]">
                        {r.kind === 'pdf' || r.kind === 'text'
                          ? `${(r.char_count || 0).toLocaleString()} chars read`
                          : (r.kind || 'file')}
                        {r.note ? ` · ${r.note}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => onToggle(r)}
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                        r.enabled === false ? 'bg-[#F1F5F9] text-[#94A3B8]' : 'bg-[#ECFDF5] text-[#059669]'
                      }`}
                      title={r.enabled === false ? 'Disabled — click to use in generations' : 'Active — used in generations'}
                      data-testid={`reference-toggle-enabled-${r.reference_id}`}
                    >
                      {r.enabled === false ? 'Off' : 'On'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(r.reference_id)}
                      aria-label={`Delete ${r.original_filename}`}
                      className="text-[#94A3B8] hover:text-red-600 transition-colors"
                      data-testid={`reference-delete-${r.reference_id}`}
                    >
                      <Trash2 size={13} aria-hidden="true" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {refs.length > 0 && (
            <p className="text-[10px] text-[#94A3B8]">{enabledCount} active · feeds up to ~8,000 chars per generation</p>
          )}
        </div>
      )}
    </div>
  );
}

export default BrandReferenceLibrary;
